import { useState } from 'react';

interface ComparisonFeature {
  name: string;
  kaspersky_standard: string | boolean;
  kaspersky_plus: string | boolean;
  kaspersky_premium: string | boolean;
  mcafee: string | boolean;
  eset: string | boolean;
}

const comparisonData: ComparisonFeature[] = [
  {
    name: 'Protección en tiempo real',
    kaspersky_standard: true,
    kaspersky_plus: true,
    kaspersky_premium: true,
    mcafee: true,
    eset: true,
  },
  {
    name: 'Firewall',
    kaspersky_standard: false,
    kaspersky_plus: true,
    kaspersky_premium: true,
    mcafee: true,
    eset: true,
  },
  {
    name: 'VPN incluida',
    kaspersky_standard: '200 MB/día',
    kaspersky_plus: '200 MB/día',
    kaspersky_premium: 'Ilimitada',
    mcafee: false,
    eset: false,
  },
  {
    name: 'Control parental',
    kaspersky_standard: false,
    kaspersky_plus: true,
    kaspersky_premium: true,
    mcafee: true,
    eset: true,
  },
  {
    name: 'Gestor de contraseñas',
    kaspersky_standard: false,
    kaspersky_plus: true,
    kaspersky_premium: true,
    mcafee: true,
    eset: true,
  },
  {
    name: 'Protección bancaria/pagos online',
    kaspersky_standard: true,
    kaspersky_plus: true,
    kaspersky_premium: true,
    mcafee: true,
    eset: true,
  },
  {
    name: 'Anti-ransomware',
    kaspersky_standard: true,
    kaspersky_plus: true,
    kaspersky_premium: true,
    mcafee: true,
    eset: true,
  },
  {
    name: 'Número de dispositivos',
    kaspersky_standard: '1-5',
    kaspersky_plus: '1-5',
    kaspersky_premium: '1-5',
    mcafee: '1-5',
    eset: '1-5',
  },
  {
    name: 'Duración disponible',
    kaspersky_standard: '1 año',
    kaspersky_plus: '2 años',
    kaspersky_premium: '1 año',
    mcafee: '1 año',
    eset: '2 años',
  },
  {
    name: 'Precio desde (1 dispositivo)',
    kaspersky_standard: '$115.600',
    kaspersky_plus: '$143.750',
    kaspersky_premium: '$173.100',
    mcafee: '$97.750',
    eset: '$109.250',
  },
  {
    name: 'Rendimiento del sistema',
    kaspersky_standard: 'Bajo impacto',
    kaspersky_plus: 'Bajo impacto',
    kaspersky_premium: 'Bajo impacto',
    mcafee: 'Medio impacto',
    eset: 'Bajo impacto',
  },
];

const products = [
  { id: 'kaspersky_standard', name: 'Kaspersky Standard', recommended: false },
  { id: 'kaspersky_plus', name: 'Kaspersky Plus', recommended: false },
  { id: 'kaspersky_premium', name: 'Kaspersky Premium', recommended: true },
  { id: 'mcafee', name: 'McAfee Total Protection', recommended: false },
  { id: 'eset', name: 'ESET NOD32', recommended: false },
];

export default function AntivirusComparison() {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([
    'kaspersky_standard',
    'kaspersky_plus',
    'kaspersky_premium',
  ]);

  const handleProductToggle = (productId: string) => {
    if (selectedProducts.includes(productId)) {
      if (selectedProducts.length > 2) {
        setSelectedProducts(selectedProducts.filter((id) => id !== productId));
      }
    } else {
      if (selectedProducts.length < 4) {
        setSelectedProducts([...selectedProducts, productId]);
      }
    }
  };

  const renderValue = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <i className="ri-check-line text-2xl text-emerald-600"></i>
      ) : (
        <i className="ri-close-line text-2xl text-gray-400"></i>
      );
    }
    return <span className="text-sm font-medium text-gray-900">{value}</span>;
  };

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Compara Antivirus
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Encuentra la protección perfecta para tus dispositivos. Compara características, precios y rendimiento de las mejores soluciones antivirus del mercado.
          </p>
        </div>

        {/* Product Selector */}
        <div className="mb-8 bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Selecciona productos para comparar (2-4):
          </h3>
          <div className="flex flex-wrap gap-3">
            {products.map((product) => (
              <button
                key={product.id}
                onClick={() => handleProductToggle(product.id)}
                className={`px-5 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                  selectedProducts.includes(product.id)
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                type="button"
              >
                {product.name}
                {product.recommended && (
                  <span className="ml-2 text-xs bg-amber-400 text-gray-900 px-2 py-0.5 rounded-full">
                    Mejor Protección
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-teal-600 to-teal-700">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white sticky left-0 bg-teal-600 z-10">
                    Características
                  </th>
                  {products
                    .filter((p) => selectedProducts.includes(p.id))
                    .map((product) => (
                      <th
                        key={product.id}
                        className={`px-6 py-4 text-center text-sm font-semibold text-white min-w-[180px] ${
                          product.recommended ? 'relative' : ''
                        }`}
                      >
                        {product.recommended && (
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <span className="bg-amber-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full shadow-lg whitespace-nowrap">
                              Mejor Protección
                            </span>
                          </div>
                        )}
                        <div className={product.recommended ? 'mt-3' : ''}>
                          {product.name}
                        </div>
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((feature, index) => (
                  <tr
                    key={feature.name}
                    className={`${
                      index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                    } hover:bg-teal-50 transition-colors`}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-inherit z-10">
                      {feature.name}
                    </td>
                    {products
                      .filter((p) => selectedProducts.includes(p.id))
                      .map((product) => (
                        <td
                          key={product.id}
                          className={`px-6 py-4 text-center ${
                            product.recommended ? 'bg-teal-50/50' : ''
                          }`}
                        >
                          {renderValue(
                            feature[product.id as keyof ComparisonFeature] as
                              | string
                              | boolean
                          )}
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recommendation Notes */}
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-user-line text-2xl text-white"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">
              Usuario Básico
            </h4>
            <p className="text-sm text-gray-700 mb-3">
              Si solo necesitas protección esencial para navegación y correo electrónico.
            </p>
            <p className="text-sm font-semibold text-blue-700">
              Recomendado: McAfee o Kaspersky Standard
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-shield-user-line text-2xl text-white"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">
              Usuario Avanzado
            </h4>
            <p className="text-sm text-gray-700 mb-3">
              Para quienes realizan compras online, banca digital y necesitan privacidad.
            </p>
            <p className="text-sm font-semibold text-purple-700">
              Recomendado: Kaspersky Plus o ESET NOD32
            </p>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200">
            <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-shield-star-line text-2xl text-white"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">
              Protección Familiar
            </h4>
            <p className="text-sm text-gray-700 mb-3">
              Máxima seguridad con VPN ilimitada, control parental y protección completa.
            </p>
            <p className="text-sm font-semibold text-amber-700">
              Recomendado: Kaspersky Premium
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-200">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-xl text-white"></i>
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                ¿Cómo elegir el mejor antivirus?
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong>Kaspersky</strong> ofrece la mejor protección del mercado con bajo impacto en el rendimiento. 
                <strong> McAfee</strong> es ideal para presupuestos ajustados con protección sólida. 
                <strong> ESET NOD32</strong> destaca por su velocidad y eficiencia en sistemas con recursos limitados. 
                Todos incluyen protección en tiempo real, anti-ransomware y actualizaciones automáticas.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}